import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../../lib/api';
import { useInvalidateSession, useSession } from '../../lib/session';

interface Props {
  /** 强制改密（首次登录）与自助改密（设置页）复用同一个表单 */
  readonly forced?: boolean;
}

export function ChangePasswordPage({ forced = false }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const invalidateSession = useInvalidateSession();
  const { data: session } = useSession();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    if (newPassword.length < 12) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch('/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      await invalidateSession();
      if (forced) navigate('/dashboard', { replace: true });
      else setSuccess(true);
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div
      className={forced ? 'auth-page__card card' : 'card'}
      style={forced ? undefined : { maxWidth: 420 }}
    >
      {forced && <h1 className="auth-page__brand">{t('auth.changePasswordTitle')}</h1>}
      {forced && <p className="field-hint">{t('auth.mustChangePasswordNotice')}</p>}
      <form onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span>{t('auth.currentPassword')}</span>
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>{t('auth.newPassword')}</span>
          <input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>{t('auth.confirmPassword')}</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </label>
        {error && <p className="field-error">{error}</p>}
        {success && <p className="badge badge--success">{t('auth.changePasswordSuccess')}</p>}
        <button type="submit" className="btn btn--primary" disabled={submitting}>
          {t('auth.changePasswordSubmit')}
        </button>
      </form>
    </div>
  );

  if (!forced) return content;
  if (session === null) return <></>;
  return <div className="auth-page">{content}</div>;
}
